/*
 * Money / currency helpers. Default currency is GBP per the platform's
 * default. Pass a currency code on any call to override.
 */

export const DEFAULT_CURRENCY = "GBP";

export interface FormatMoneyOptions {
  /** Use compact notation (e.g. £12.4k) for values >= 10,000. */
  compact?: boolean;
  /** Show sign for positive numbers (e.g. "+£100"). */
  showSign?: boolean;
}

export function formatMoney(
  amount: number,
  currency: string = DEFAULT_CURRENCY,
  opts: FormatMoneyOptions = {},
): string {
  const useCompact = opts.compact && Math.abs(amount) >= 10_000;
  const formatted = new Intl.NumberFormat(undefined, {
    style: "currency",
    currency,
    notation: useCompact ? "compact" : "standard",
    maximumFractionDigits: useCompact ? 1 : 2,
    minimumFractionDigits: useCompact ? 0 : 2,
    signDisplay: opts.showSign ? "always" : "auto",
  }).format(amount);
  return formatted;
}

/** Currency symbol only (for tight UI like a column header). */
export function currencySymbol(currency: string = DEFAULT_CURRENCY): string {
  const map: Record<string, string> = {
    GBP: "£", USD: "$", EUR: "€", JPY: "¥",
    CAD: "C$", AUD: "A$", CHF: "CHF",
  };
  return map[currency] ?? currency;
}

/** A compact, tabular-numerals friendly relative date. */
export function formatRelativeDate(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const days = Math.floor(ms / 86400000);
  if (days < 0) {
    const future = -days;
    if (future === 0) return "today";
    if (future === 1) return "in 1d";
    if (future < 30) return `in ${future}d`;
    const months = Math.floor(future / 30);
    return months < 12 ? `in ${months}mo` : `in ${Math.floor(months / 12)}y`;
  }
  if (days < 1) return "today";
  if (days === 1) return "1d ago";
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
}

export function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric", month: "short", day: "numeric",
  });
}

export const SUPPORTED_CURRENCIES = ["GBP", "USD", "EUR", "JPY"] as const;
export type Currency = (typeof SUPPORTED_CURRENCIES)[number];
